import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/config/brand";
import { CheckCircle2, Sparkles, GraduationCap, Upload, Loader2, Award, X } from "lucide-react";
import { Link } from "react-router-dom";

const LANG_CHOICES = [
  { key: "en", label: "English" },
  { key: "hi", label: "हिन्दी (Hindi)" },
  { key: "both", label: "Bilingual (EN + HI)" },
];

const QUALIFICATION_QUESTIONS = [
  { key: "rci", question: "Are you an RCI Registered Professional (Rehabilitation Council of India)?" },
  { key: "postgrad", question: "Do you hold a postgraduate degree in Counselling / Counseling Psychology / Guidance & Counselling, or a related field?" },
  { key: "diploma_masters", question: "Do you hold a Diploma / PG Diploma in Counselling along with a Master's in Psychology?" },
  { key: "grad_diploma", question: "Do you hold a Graduation in Psychology along with a Diploma/Certificate in Counselling and relevant experience?" },
];

export const QUALIFICATION_LABELS = {
  rci: "RCI Registered Professional",
  postgrad: "Postgraduate in Counselling / Psychology",
  diploma_masters: "Diploma/PG Diploma in Counselling + Master's in Psychology",
  grad_diploma: "Graduation in Psychology + Diploma/Certificate in Counselling",
  other: "Other qualification",
};

function formatExperience(months) {
  const m = Number(months);
  if (!m || m < 0) return null;
  if (m < 12) return `${m} month${m === 1 ? "" : "s"} experience`;
  const yrs = Math.floor(m / 12);
  const rem = m % 12;
  if (rem === 0) return `${yrs}+ yr${yrs === 1 ? "" : "s"} experience`;
  return `${yrs}y ${rem}m experience`;
}

function resolvePhotoSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Apply() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const bypassActive = !!user?.is_lead && sessionStorage.getItem("counseltaria_bypass_generate") === "1";
  const [genName, setGenName] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [f, setF] = useState({
    phone: "", specialty: "", experience_months: 6, bio: "",
    photo_url: "", cv_url: "", intro_video_url: "",
    why_join: "", languages: ["en"], agreed_code: false,
    qualification_path: null, qualification_certificate_urls: [], other_qualification_details: "",
  });
  const [qStep, setQStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [certUploading, setCertUploading] = useState(false);
  const [cvUploading, setCvUploading] = useState(false);
  const [err, setErr] = useState("");
  const [existingApp, setExistingApp] = useState(null);
  const [eligibility, setEligibility] = useState(null); // null=loading, {blocked, domain}
  const fileInputRef = useRef(null);
  const certFileInputRef = useRef(null);
  const cvFileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      api.get("/me/apply-eligibility?target=counselor")
        .then(r => setEligibility(r.data))
        .catch(() => setEligibility({ blocked: false, domain: null }));
      api.get("/counselor-applications/mine")
        .then(r => setExistingApp(r.data))
        .catch(err => {
          // 404 = no application yet, that's expected; only log others
          if (err?.response?.status && err.response.status !== 404) {
            console.warn("Failed to load existing application:", err?.message || err);
          }
        });
    }
  }, [user]);

  const set = (k, v) => { setF(prev => ({ ...prev, [k]: v })); setErr(""); };
  const toggleLang = (l) => {
    let cur = f.languages || [];
    if (l === "both") {
      cur = cur.includes("both") ? [] : ["both"];
    } else {
      cur = cur.filter(x => x !== "both");
      cur = cur.includes(l) ? cur.filter(x => x !== l) : [...cur, l];
    }
    set("languages", cur.length ? cur : ["en"]);
  };

  const answerNo = () => {
    setErr("");
    if (qStep < QUALIFICATION_QUESTIONS.length) setQStep(qStep + 1);
    else setF(prev => ({ ...prev, qualification_path: "other", experience_months: 0 }));
  };
  const resetQualification = () => {
    setErr("");
    setF(prev => ({ ...prev, qualification_path: null, qualification_certificate_urls: [], cv_url: "", other_qualification_details: "", experience_months: 6 }));
    setQStep(1);
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Photo is too large (max 6MB)"); return; }
    setUploading(true);
    try {
      const body = new FormData(); body.append("file", file);
      const { data } = await api.post("/uploads/photo", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", data.url);
      toast.success("Photo uploaded");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setUploading(false); }
  };

  const onPickCertificate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File is too large (max 10MB)"); return; }
    setCertUploading(true);
    try {
      const body = new FormData(); body.append("file", file);
      const { data } = await api.post("/uploads/document", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("qualification_certificate_urls", [...f.qualification_certificate_urls, data.url]);
      toast.success("Certificate uploaded");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setCertUploading(false); e.target.value = ""; }
  };
  const removeCert = (url) => set("qualification_certificate_urls", f.qualification_certificate_urls.filter(u => u !== url));

  const onPickCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File is too large (max 10MB)"); return; }
    setCvUploading(true);
    try {
      const body = new FormData(); body.append("file", file);
      const { data } = await api.post("/uploads/document", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("cv_url", data.url);
      toast.success("CV uploaded");
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setCvUploading(false); e.target.value = ""; }
  };

  const monthsHint = useMemo(() => formatExperience(f.experience_months) || "—", [f.experience_months]);
  const canSubmit = f.agreed_code && !busy && !uploading && !certUploading && !cvUploading;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agreed_code) { setErr("Please agree to the code of conduct."); return; }
    if (!f.qualification_path) { setErr("Please answer the qualification questions above."); return; }
    if (f.qualification_path === "other") {
      if (!f.cv_url) { setErr("CV upload is mandatory for the Other qualification category."); return; }
      if (!f.other_qualification_details.trim()) { setErr("Please describe your other qualification details."); return; }
    } else if (f.qualification_certificate_urls.length === 0) {
      setErr("Please upload your qualification certificate."); return;
    }
    setBusy(true); setErr("");
    try {
      const payload = { ...f, experience_months: Number(f.experience_months) };
      await api.post("/counselor-applications", payload);
      toast.success("Application submitted — track status on your dashboard");
      nav("/dashboard");
    } catch (er) { setErr(formatApiError(er)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-16 text-center text-sm">Loading…</div>;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="card-soft p-10">
          <Sparkles className="w-10 h-10 mx-auto" style={{color:"var(--brand)"}}/>
          <h1 className="font-serif-display text-3xl mt-4">Sign in to apply</h1>
          <p className="text-sm mt-3" style={{color:"var(--text-secondary)"}}>
            Verify your identity with Google first — no password to create — then fill in your application.
          </p>
          <Link data-testid="apply-signin-btn" to="/login?next=/apply" className="btn-primary mt-8 inline-block">Continue with Google</Link>
        </div>
      </div>
    );
  }

  if (bypassActive) {
    if (genResult) {
      return (
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <div className="card-soft p-10">
            <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: "var(--brand)" }} />
            <h1 className="font-serif-display text-3xl mt-4" data-testid="generate-success-message">
              "{genResult.name}" created
            </h1>
            <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
              Live immediately — no login of their own. Manage them from your dashboard's "View as".
            </p>
            <div className="flex flex-col gap-3 mt-8">
              <button data-testid="generate-another-btn"
                onClick={() => { setGenResult(null); setGenName(""); }}
                className="btn-primary">Generate another</button>
              <button data-testid="generate-done-btn"
                onClick={() => { sessionStorage.removeItem("counseltaria_bypass_generate"); nav("/counselor"); }}
                className="btn-outline">Done — back to dashboard</button>
            </div>
          </div>
        </div>
      );
    }
    const submitGenerate = async (e) => {
      e.preventDefault();
      if (!genName.trim()) { toast.error("Please enter a name."); return; }
      setGenBusy(true);
      try {
        const { data } = await api.post("/admin/generate-counselor", { name: genName.trim() });
        toast.success("Counselor account created");
        setGenResult(data);
      } catch (er) { toast.error(formatApiError(er)); }
      finally { setGenBusy(false); }
    };
    return (
      <div className="max-w-xl mx-auto px-6 py-14">
        <span className="label-eyebrow inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Admin bypass
        </span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">Generate a counselor account.</h1>
        <p className="text-sm max-w-lg mb-8" style={{ color: "var(--text-secondary)" }}>
          Everything else is filled in automatically and approval is skipped — the account is
          live the moment you submit. It has no login of its own; manage it from your dashboard.
        </p>
        <form onSubmit={submitGenerate} className="card-soft p-8 space-y-5" data-testid="generate-counselor-form">
          <div>
            <label className="label-eyebrow block mb-2">Counselor's name</label>
            <input required autoFocus data-testid="generate-counselor-name" value={genName}
              onChange={e => setGenName(e.target.value)} placeholder="e.g. Riya Sharma"
              className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
          </div>
          <button type="submit" data-testid="generate-counselor-submit" disabled={genBusy}
            className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed">
            {genBusy ? "Creating…" : "Create counselor account"}
          </button>
          <button type="button" data-testid="generate-cancel-btn"
            onClick={() => { sessionStorage.removeItem("counseltaria_bypass_generate"); nav("/counselor"); }}
            className="btn-outline w-full">Cancel</button>
        </form>
      </div>
    );
  }

  if (user && user.role === "counselor" && !user.is_lead) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="card-soft p-10">
          <CheckCircle2 className="w-10 h-10 mx-auto" style={{color:"var(--brand)"}}/>
          <h1 className="font-serif-display text-3xl mt-4" data-testid="apply-already-counselor-message">You're already a {BRAND.shortName} counselor 💚</h1>
          <Link data-testid="apply-already-counselor-back" to="/counselor" className="btn-primary mt-8 inline-block">Go to dashboard</Link>
        </div>
      </div>
    );
  }

  // Cross-domain block: identity is already tied to another role/domain
  if (user && eligibility?.blocked) {
    const domainLabel = { client: "client", employee: "employee", "employee applicant": "employee applicant", admin: "admin" }[eligibility.domain] || eligibility.domain;
    const article = /^[aeiou]/i.test(domainLabel) ? "an" : "a";
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="card-soft p-10">
          <h1 className="font-serif-display text-3xl mt-4">Already registered as {article} {domainLabel}</h1>
          <p className="text-sm mt-3" data-testid="apply-cross-domain-message" style={{color:"var(--text-secondary)"}}>
            You are already registered as {article} {domainLabel}. Kindly go back and choose another Google account to continue to the counselor application.
          </p>
          <Link data-testid="apply-cross-domain-back" to="/dashboard" className="btn-primary mt-8 inline-block">Go back to dashboard</Link>
        </div>
      </div>
    );
  }

  // Existing application on file
  if (user && existingApp && existingApp.status !== "rejected") {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="card-soft p-10">
          <h1 className="font-serif-display text-3xl">Your application is on file</h1>
          <p className="text-sm mt-3" style={{color:"var(--text-secondary)"}}>
            Track its status, and any payment instructions, on your dashboard.
          </p>
          <Link to="/dashboard" className="btn-primary mt-8 inline-block">View status</Link>
        </div>
      </div>
    );
  }

  if (eligibility === null) return <div className="p-16 text-center text-sm">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-14">
      <span className="label-eyebrow inline-flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5"/> Join the team
      </span>
      <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-3">Apply to counsel with {BRAND.shortName}.</h1>
      <p className="text-sm max-w-2xl mb-8" style={{color:"var(--text-secondary)"}}>
        Applying as <strong>{user.name}</strong> ({user.email}). Track your review status on your dashboard.
      </p>

      <form onSubmit={submit} className="card-soft p-8 space-y-5">
        <div>
          <label className="label-eyebrow block mb-2">Phone (optional)</label>
          <input data-testid="apply-phone" value={f.phone} onChange={e=>set("phone", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Specialty / focus</label>
          <input required data-testid="apply-specialty" value={f.specialty} onChange={e=>set("specialty", e.target.value)}
            placeholder="e.g. Anxiety & exam stress, family dynamics, LGBTQ+ support"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Languages you counsel in</label>
          <div className="flex gap-2 flex-wrap">
            {LANG_CHOICES.map(l => (
              <button type="button" key={l.key} onClick={()=>toggleLang(l.key)}
                data-selected={f.languages.includes(l.key)} data-testid={`apply-lang-${l.key}`}
                className="slot-btn">{l.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Short bio</label>
          <textarea required data-testid="apply-bio" rows={4} value={f.bio} onChange={e=>set("bio", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
        </div>

        {/* --- Qualification & credentials --- */}
        <div className="border-t border-[color:var(--border-soft)] pt-6">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4" style={{color:"var(--brand)"}}/>
            <h2 className="font-serif-display text-xl">Qualification & credentials</h2>
          </div>
          <p className="text-xs mb-5" style={{color:"var(--text-secondary)"}}>
            Answer a few quick questions so we can route your application to the right review track.
          </p>

          {!f.qualification_path ? (
            <div className="rounded-2xl p-5" style={{background:"var(--bg-alt)"}} data-testid="apply-qualification-step">
              <div className="text-xs mb-3" style={{color:"var(--text-secondary)"}}>
                Question {qStep} of {QUALIFICATION_QUESTIONS.length}
              </div>
              <div className="font-serif-display text-lg mb-4">{QUALIFICATION_QUESTIONS[qStep - 1].question}</div>
              <div className="flex gap-3">
                <button type="button" data-testid="apply-qualification-yes"
                  onClick={()=>set("qualification_path", QUALIFICATION_QUESTIONS[qStep - 1].key)}
                  className="btn-primary text-sm">Yes</button>
                <button type="button" data-testid="apply-qualification-no" onClick={answerNo}
                  className="btn-outline text-sm">No</button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl p-5" style={{background:"var(--bg-alt)"}} data-testid="apply-qualification-resolved">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <span className="chip text-xs" style={{background:"rgba(91,117,83,0.15)", color:"var(--brand)"}}>
                  {QUALIFICATION_LABELS[f.qualification_path]}
                </span>
                <button type="button" data-testid="apply-qualification-change" onClick={resetQualification}
                  className="text-xs underline" style={{color:"var(--text-secondary)"}}>Change answer</button>
              </div>

              {f.qualification_path === "other" ? (
                <div className="space-y-4">
                  <div>
                    <label className="label-eyebrow block mb-2">CV / resume (required)</label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input ref={cvFileInputRef} type="file" accept=".pdf,image/*" onChange={onPickCv} className="hidden" data-testid="apply-cv-file"/>
                      <button type="button" onClick={()=>cvFileInputRef.current?.click()} disabled={cvUploading} className="btn-outline text-sm">
                        {cvUploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>} {cvUploading ? "Uploading…" : "Upload CV"}
                      </button>
                      <button type="button" data-testid="apply-cv-chatgpt"
                        onClick={()=>window.open("https://chatgpt.com", "_blank", "noopener,noreferrer")}
                        className="btn-outline text-sm">
                        <Sparkles className="w-4 h-4"/> Create CV with ChatGPT
                      </button>
                    </div>
                    {f.cv_url && (
                      <div className="text-xs mt-2 flex items-center gap-2" style={{color:"var(--brand)"}}>
                        <CheckCircle2 className="w-3.5 h-3.5"/> CV uploaded —
                        <a href={f.cv_url} target="_blank" rel="noreferrer" className="underline">view</a>
                        <button type="button" onClick={()=>set("cv_url","")} className="underline inline-flex items-center gap-0.5" style={{color:"var(--accent)"}}><X className="w-3 h-3"/>remove</button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">Other qualification details (required)</label>
                    <textarea rows={4} data-testid="apply-other-qualification" value={f.other_qualification_details}
                      onChange={e=>set("other_qualification_details", e.target.value)}
                      placeholder="e.g. 12th subjects, graduation/PG subjects, diploma field, certificates held, and relevant experience"
                      className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="label-eyebrow block mb-2">Qualification certificate{f.qualification_certificate_urls.length !== 1 ? "s" : ""} (required)</label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <input ref={certFileInputRef} type="file" accept=".pdf,image/*" onChange={onPickCertificate} className="hidden" data-testid="apply-certificate-file"/>
                      <button type="button" onClick={()=>certFileInputRef.current?.click()} disabled={certUploading} className="btn-outline text-sm">
                        {certUploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>} {certUploading ? "Uploading…" : "Upload certificate"}
                      </button>
                      <span className="text-xs" style={{color:"var(--text-secondary)"}}>PDF or image · up to 10MB · add more than one if needed</span>
                    </div>
                    {f.qualification_certificate_urls.length > 0 && (
                      <ul className="mt-3 space-y-1" data-testid="apply-certificate-list">
                        {f.qualification_certificate_urls.map((u, i) => (
                          <li key={u} className="text-xs flex items-center gap-2" style={{color:"var(--brand)"}}>
                            <a href={u} target="_blank" rel="noreferrer" className="underline">Certificate {i + 1} ↗</a>
                            <button type="button" onClick={()=>removeCert(u)} className="underline inline-flex items-center gap-0.5" style={{color:"var(--accent)"}}><X className="w-3 h-3"/>remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">Experience</label>
                    <input type="number" min="0" max="720" required data-testid="apply-experience"
                      value={f.experience_months} onChange={e=>set("experience_months", e.target.value)}
                      className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
                    <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>Months of experience · {monthsHint}</div>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">CV / resume (optional)</label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input ref={cvFileInputRef} type="file" accept=".pdf,image/*" onChange={onPickCv} className="hidden" data-testid="apply-cv-file"/>
                      <button type="button" onClick={()=>cvFileInputRef.current?.click()} disabled={cvUploading} className="btn-outline text-sm">
                        {cvUploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>} {cvUploading ? "Uploading…" : (f.cv_url ? "Replace CV" : "Upload CV")}
                      </button>
                      {f.cv_url && <button type="button" onClick={()=>set("cv_url","")} className="text-xs underline" style={{color:"var(--accent)"}}>Remove</button>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="label-eyebrow block mb-2">Headshot photo (upload)</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0" style={{background:"var(--bg-alt)"}}>
              {f.photo_url ? <img src={resolvePhotoSrc(f.photo_url)} alt="Photo" className="w-full h-full object-cover"/>
                : <div className="w-full h-full flex items-center justify-center text-2xl font-serif-display" style={{color:"var(--brand)"}}>
                    {(user?.name || f.name || "?")?.[0]?.toUpperCase()}
                  </div>}
            </div>
            <div className="flex-1">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickPhoto} className="hidden" data-testid="apply-photo-file"/>
              <button type="button" onClick={()=>fileInputRef.current?.click()} disabled={uploading} className="btn-outline text-sm">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                {uploading ? "Uploading…" : f.photo_url ? "Replace photo" : "Upload photo"}
              </button>
              {f.photo_url && <button type="button" onClick={()=>set("photo_url","")} className="text-xs ml-3" style={{color:"var(--accent)"}}>Remove</button>}
              <div className="text-xs mt-2" style={{color:"var(--text-secondary)"}}>JPEG/PNG/WebP · up to 6MB · auto-cropped to a square.</div>
            </div>
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Intro video URL (optional)</label>
          <input data-testid="apply-video" value={f.intro_video_url} onChange={e=>set("intro_video_url", e.target.value)}
            placeholder="Loom / YouTube unlisted link"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Why do you want to counsel with Counseltaria? (optional)</label>
          <textarea data-testid="apply-why" rows={3} value={f.why_join} onChange={e=>set("why_join", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]"/>
        </div>
        <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="apply-code">
          <input type="checkbox" checked={f.agreed_code} onChange={e=>set("agreed_code", e.target.checked)} className="mt-1 w-4 h-4 accent-[color:var(--brand)]"/>
          <span style={{color:"var(--text-secondary)"}}>
            I agree to the code of conduct: confidentiality, respectful communication, honest credentials, timely attendance, and mandatory escalation of safeguarding concerns.
          </span>
        </label>
        {err && <div data-testid="apply-error" className="text-sm text-[color:var(--accent)]">{err}</div>}
        <button data-testid="apply-submit" disabled={!canSubmit} className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed">
          <GraduationCap className="w-4 h-4"/> {busy ? "Submitting…" : (user ? "Submit application" : "Create account & submit")}
        </button>
      </form>
    </div>
  );
}
